import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Alert, AlertProps } from '@m/alchemy-ui/Alert';
import { Button } from '@m/alchemy-ui/Button';
import { Text } from '@m/alchemy-ui/Text';
import { Toast } from '@m/alchemy-ui/Toast';
import { ToastMessageProps } from '@m/alchemy-ui/ToastMessage';
import { units } from '@m/alchemy-theme';
import {
  GetOwnProfileDocument,
  GetOwnProfileQuery,
  ProfileApprovalStatusEnum,
  useGetOwnProfileQuery,
  useUpdateOwnProfileMutation,
} from 'graphql.types';
import { AlchemyLoading } from 'common/alchemyComponents/Loading';
import SliderModal from 'common/components/SliderModal/SliderModal';
import { formatErrors } from 'common/utils/helpers';
import { Title } from 'admin/company/components/ManageTeam/Styled/Styled';
import {
  Label,
  ModalContainer,
  ModalContentWrapper,
  ModalFooter,
  ModalHeading,
} from 'admin/company/components/ManageTeam/Users/fields/StyledElements';
import {
  firstName,
  homeTown,
  lastName,
  profileBio,
  profileImage,
  stateOrCountry,
} from 'admin/company/constants/usersConstants';
import ProfileForm from './ProfileForm';
import { useProfileFormRequiredFields } from './hooks/useProfileFormRequiredFields';

type StatusAlertConfig = { level: AlertProps['level']; message: string };
// formatErrors describes the error shape structurally rather than as an ApolloError.
const toToastMessages = (error: unknown): ToastMessageProps[] =>
  formatErrors(error as Parameters<typeof formatErrors>[0]) as ToastMessageProps[];

// Wording mirrors the profiles.edit entries in config/locales/en.yml, which the
// legacy self-service profile page still uses. An approved and complete profile
// needs no messaging, so it has no entry.
const APPROVAL_STATUS_ALERTS: Partial<Record<ProfileApprovalStatusEnum, StatusAlertConfig>> = {
  [ProfileApprovalStatusEnum.CompletePendingApproval]: {
    level: 'warning',
    message:
      'Your profile is complete, but needs to be approved by a team leader before you can send requests.',
  },
  [ProfileApprovalStatusEnum.Incomplete]: {
    level: 'error',
    message:
      'Your profile must be completed and approved by a team leader before you can send requests.',
  },
  [ProfileApprovalStatusEnum.PendingApproval]: {
    level: 'warning',
    message:
      'You have a profile change that is pending approval from a team leader. Until it is approved, ' +
      'your previous profile will be used for all feedback requests.',
  },
};

const StatusAlert = styled.div`
  margin-bottom: ${units(1)};
`;

const ReadOnlyField = styled.div`
  margin-bottom: ${units(1)};
`;

interface EditOwnProfileProps {
  isOpen: boolean;
  onClose: () => void;
}

function toFormValues(ownProfile: GetOwnProfileQuery['ownProfile']): FieldValues {
  const { profile } = ownProfile;

  return {
    // Only a custom image is a real value; imageUrl otherwise falls back to a
    // default avatar, which is not a valid image to save back.
    [profileImage.key]: profile?.hasCustomImage ? profile.imageUrl : '',
    [firstName.key]: profile?.firstName,
    [lastName.key]: profile?.lastName,
    [homeTown.key]: profile?.hometown,
    [stateOrCountry.key]: profile?.stateOrCountry,
    [profileBio.key]: profile?.text,
  };
}

export const EditOwnProfile = ({ isOpen, onClose }: EditOwnProfileProps) => {
  const [messages, setMessages] = useState<ToastMessageProps[]>([]);
  const methods = useForm<FieldValues>();
  const { control, handleSubmit, reset } = methods;

  const { data, loading, error } = useGetOwnProfileQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isOpen,
  });
  const ownProfile = data?.ownProfile;

  useEffect(() => {
    if (ownProfile) {
      reset(toFormValues(ownProfile));
    }
  }, [ownProfile, reset]);

  useEffect(() => {
    if (error) {
      setMessages(toToastMessages(error));
    }
  }, [error]);

  const [updateOwnProfile, { loading: saving }] = useUpdateOwnProfileMutation({
    onError: err => setMessages(toToastMessages(err)),
    // The payload carries the resulting approval status, so the messaging is up to
    // date the next time the slide-over opens without another request.
    update: (cache, { data }) => {
      if (!data?.updateOwnProfile) return;

      cache.writeQuery({
        query: GetOwnProfileDocument,
        data: { ownProfile: data.updateOwnProfile },
      });
    },
    onCompleted: () => onClose(),
  });

  const requiredFields = useProfileFormRequiredFields({
    feedbackProductEnabled: Boolean(ownProfile?.feedbackProductEnabled),
    integratedEmployees: Boolean(ownProfile?.integratedEmployees),
  });

  const onSubmit = (values: FieldValues) => {
    setMessages([]);
    updateOwnProfile({
      variables: {
        input: {
          attributes: {
            firstName: values[firstName.key],
            lastName: values[lastName.key],
            hometown: values[homeTown.key],
            stateOrCountry: values[stateOrCountry.key],
            text: values[profileBio.key],
            image: values[profileImage.key],
          },
        },
      },
    });
  };

  const statusAlert = ownProfile && APPROVAL_STATUS_ALERTS[ownProfile.approvalStatus];

  return (
    <SliderModal isOpen={isOpen} closeModal={onClose}>
      <ModalContainer>
        <ModalContentWrapper>
          <ModalHeading>
            <Title>Edit My Profile</Title>
            <Toast messages={messages} />
          </ModalHeading>

          {loading || saving ?
            <AlchemyLoading fullPage />
          : !ownProfile ?
            <Alert level="warning" size="small">
              Error loading profile
            </Alert>
          : <>
              {statusAlert && (
                <StatusAlert>
                  <Alert level={statusAlert.level} size="small">
                    {statusAlert.message}
                  </Alert>
                </StatusAlert>
              )}

              {ownProfile.email && (
                <ReadOnlyField>
                  <Label>Email</Label>
                  <Text as="div" data-notranslate>
                    {ownProfile.email}
                  </Text>
                </ReadOnlyField>
              )}

              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <ProfileForm
                    control={control}
                    requiredFields={requiredFields}
                    hideLocation={Boolean(ownProfile.integratedEmployees)}
                    showDetails={Boolean(ownProfile.feedbackProductEnabled)}
                    bioGuidance={ownProfile.guidelineProfile || undefined}
                  />
                </form>
              </FormProvider>
            </>
          }
        </ModalContentWrapper>
      </ModalContainer>

      <ModalFooter className="modal-footer">
        <Button priority="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" onClick={handleSubmit(onSubmit)} disabled={loading || saving}>
          Save
        </Button>
      </ModalFooter>
    </SliderModal>
  );
};

export default EditOwnProfile;
